import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'

import * as styles from './PropertyList.module.scss'

import { NavigateLinkButton, PropertyCard } from '../../../components'
import { MASTERSVARUS_FACILITY, PATH } from '../../../constants'
import { useCreateNextPath, useProperties } from '../../../hooks'

type CardProps = {
  name: string
  description: string
  images: string[]
  className: string
  access?: string
  pricing?: string
  property?: string
  direction?: 'horizontal' | 'vertical'
  showFitScreenIcon?: boolean
  externalUrl?: string
}

const Card: React.FC<CardProps> = ({
  name,
  description,
  images,
  access,
  pricing,
  className,
  property,
  showFitScreenIcon = false,
  direction = 'vertical',
  externalUrl,
}) => {
  const navigate = useNavigate()
  const { onCreateNextPath } = useCreateNextPath()

  return (
    <div className={clsx(className, styles.card)}>
      <PropertyCard
        images={images}
        title={name}
        access={access}
        pricing={pricing}
        property={property}
        direction={direction}
        showFitScreenIcon={showFitScreenIcon}
        onClick={() => {
          if (externalUrl) {
            window.open(externalUrl, '_blank', 'noopener,noreferrer')
          } else if (property) {
            navigate(
              onCreateNextPath(PATH.property, {
                pathParams: { property },
                queryParams: { page: undefined },
              })
            )
          }
        }}
      >
        <p className={styles.desc}>{description}</p>
      </PropertyCard>
    </div>
  )
}

const translateNamespace = 'components.TopIndexFrame.propertyList'

export const PropertyList: React.FC = () => {
  const navigate = useNavigate()
  const { properties } = useProperties()
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language as 'ja' | 'en'
  const fvillageProperties = properties
    .filter((property) => property.insideFvillage)
    .slice(0, 4)
  const partnerProperties = properties
    .filter((property) => !property.insideFvillage)
    .filter((property) =>
      ['fvillage', 'kitahiroshima', 'shinsapporo', ''].includes(
        property.hotelArea ?? ''
      )
    )
    .slice(0, 9)

  const mastersvarusData =
    MASTERSVARUS_FACILITY[currentLanguage] || MASTERSVARUS_FACILITY.ja

  return (
    <React.Fragment>
      <div className={styles.properties}>
        {fvillageProperties.map(
          ({ id, slug, name, descriptionDetail, images, access, pricing }) =>
            name &&
            slug && (
              <Card
                name={name}
                description={descriptionDetail ?? ''}
                images={images}
                access={access}
                pricing={pricing}
                property={slug}
                className={styles.property}
                key={id}
                direction="horizontal"
                showFitScreenIcon
              />
            )
        )}
        <Card
          name={mastersvarusData.name}
          description={mastersvarusData.description}
          images={MASTERSVARUS_FACILITY.images}
          access={mastersvarusData.access}
          pricing={MASTERSVARUS_FACILITY.pricing}
          property={MASTERSVARUS_FACILITY.property}
          className={styles.property}
          direction="horizontal"
          showFitScreenIcon
          externalUrl={MASTERSVARUS_FACILITY.externalUrl}
        />
      </div>

      <div className={styles.partners}>
        {partnerProperties.map(
          ({ id, slug, name, description, images, access, pricing }) =>
            name &&
            slug && (
              <Card
                name={name}
                description={description ?? ''}
                images={images}
                access={access}
                pricing={pricing}
                property={slug}
                className={styles.partner}
                key={id}
                showFitScreenIcon
              />
            )
        )}
      </div>

      <NavigateLinkButton
        text={t(`${translateNamespace}.viewFacilityList`)}
        onClick={() => navigate(PATH.list)}
      />
    </React.Fragment>
  )
}
